import { DeviceStatusRow } from "components/rules/deviceStatusRow";
import { PricingRule, PricingRuleAction } from "services/asset-management/pricing-rules/PricingRuleService";

// Changes that are not saved yet. The rules are loaded one page at a time, so changes made to rules on other pages must
// be remembered here until the user saves.
export interface PendingRuleChanges {
    // Rules whose content differs from the saved rule, by uuid.
    edited: Record<string, DeviceStatusRow>;
    // The saved version of the rules that were deleted, by uuid.
    deleted: Record<string, DeviceStatusRow>;
    created: DeviceStatusRow[];
}

export const NO_PENDING_CHANGES: PendingRuleChanges = { edited: {}, deleted: {}, created: [] };

export const hasPendingChanges = (changes: PendingRuleChanges) =>
    Object.keys(changes.edited).length > 0 || Object.keys(changes.deleted).length > 0 || changes.created.length > 0;

// The order of the selected uplift values carries no meaning.
const sameUpliftValues = (first: string[], second: string[]) => {
    const sortedSecond = [...second].sort();
    return first.length === second.length && [...first].sort().every((uuid, index) => uuid === sortedSecond[index]);
};

export const isRowChanged = (row: DeviceStatusRow, saved: DeviceStatusRow) =>
    saved.manufacturer !== row.manufacturer.trim() ||
    saved.model !== row.model.trim() ||
    saved.blanccoGrade !== row.blanccoGrade ||
    saved.value !== row.value ||
    !sameUpliftValues(saved.upliftValues, row.upliftValues);

// Merge what the table shows into the pending changes. Saved rows are the rules of the loaded page as the server
// returned them. Rows that are new on screen are added to the created rules, so the pending changes passed in must not
// contain them already.
export const collectPendingChanges = (
    pending: PendingRuleChanges,
    visibleRows: DeviceStatusRow[],
    savedRows: DeviceStatusRow[]
): PendingRuleChanges => {
    const visibleByUuid: Record<string, DeviceStatusRow> = {};
    visibleRows.forEach((row) => {
        if (row.uuid) {
            visibleByUuid[row.uuid] = row;
        }
    });
    const edited = { ...pending.edited };
    const deleted = { ...pending.deleted };
    savedRows.forEach((saved) => {
        if (!saved.uuid) {
            return;
        }
        const visible = visibleByUuid[saved.uuid];
        if (!visible) {
            delete edited[saved.uuid];
            deleted[saved.uuid] = saved;
        } else if (isRowChanged(visible, saved)) {
            edited[saved.uuid] = visible;
        } else {
            delete edited[saved.uuid];
        }
    });
    return { edited, deleted, created: [...pending.created, ...visibleRows.filter((row) => !row.uuid)] };
};

// The rows to show for a page of saved rules: deleted rules are hidden and edited rules show their unsaved content.
export const applyPendingChanges = (savedRows: DeviceStatusRow[], changes: PendingRuleChanges): DeviceStatusRow[] =>
    savedRows
        .filter((saved) => !saved.uuid || !(saved.uuid in changes.deleted))
        .map((saved) => (saved.uuid && changes.edited[saved.uuid]) || saved);

const toRule = (row: DeviceStatusRow, action: PricingRuleAction): PricingRule => ({
    uuid: row.uuid,
    manufacturer: row.manufacturer.trim(),
    model: row.model.trim(),
    blanccoGrade: row.blanccoGrade,
    value: Number(row.value),
    upliftValues: row.upliftValues.length > 0 ? row.upliftValues : undefined,
    action,
});

export const toRules = (changes: PendingRuleChanges): PricingRule[] => [
    ...Object.values(changes.deleted).map((saved): PricingRule => ({
        uuid: saved.uuid,
        manufacturer: saved.manufacturer,
        model: saved.model,
        blanccoGrade: saved.blanccoGrade,
        value: Number(saved.value),
        action: "DELETE",
    })),
    ...Object.values(changes.edited).map((row) => toRule(row, "EDIT")),
    ...changes.created.map((row) => toRule(row, "CREATE")),
];
